import { log, deepFreeze, searchObject, hasKey, getValue, normalizeString, removeAccents, updateLoadingSpinner, getTemplate, nextFrame, delay, displayLongDistance, callAdminAjax, getTranslatedString, createTranslator } from './utils.js';
import { decryptAPIData } from './decrypt.js';
import { pathfinder } from './pathfinder.js';
import { setFenixModalContent, openFenixModal, closeFenixModal, getFenixModalElement, setFenixBackdrop, getFenixModalIsOpen } from './fenixModal.js';
import { getRoutePlaces, addChildRoutePlaces, sliceRoutePlacesFromOrigin, sliceRoutePlacesToDestination, filterRoutePlacesByIndex, renderFilteredPlaceList, setGlobalPathStops, getGlobalPathStops, clearGlobalPathStops, filterInaccessiblePlaces, addCalculatedValuesFromOrigin, hideRoutePlacesBySearchQuery } from './placeList.js';

const $ = window.jQuery;

// Create a translator function for the map planner shared module.
const t = createTranslator('mapPlannerShared');

// Set global vars
let placeData = null;

// Will hold ENCRYPTED downloaded API data.
const fetchedAPIData = {
    'places': {},
    'mapserver': {},
    'listings': {},
    'friends': {}
};

// Will hold the in-progress API download promises.
const inProgressAPIDownloadPromises = {
    'places': {},
    'mapserver': {},
    'listings': {},
    'friends': {}
};

// Localize global vars
const apiConfig = window.ironman.appConfig.apis;

/**
 * Returns a frozen copy of the place data.
 * @returns {Object} The place data.
 */
export function getAllPlaceData() {
    if (!placeData) throw new Error('Place data not initialized');
    log('getAllPlaceData', placeData);
    return deepFreeze(placeData);
}

// ────────────────────────────────────────────────────────────────────────────────
  // Helper: Given placeData, produce a reversed copy so that every route’s places[]
  //    runs from “original end” to “original start,” and invert dist/asc/desc so those
  //    fields again read “remaining to new end.”
  // ────────────────────────────────────────────────────────────────────────────────
export function getReversedPlaceData() {
    placeData = getAllPlaceData();
    const reversed = {
        routes: {},
        places: placeData.places // We do not mutate place objects themselves
    };

    Object.keys(placeData.routes).forEach(rid => {
        // log('Reversing route', rid);
        // Note: I'm getting errors when routes are empty, which shouldn't happen in prod (they at least have intersections). But just to deal with it here: if length is 0, skip the route.
        if(placeData.routes[rid].places.length === 0) {
            log('Route is empty, skipping', rid);
            return;
        }
        // End note.
        
        const oldRoute     = placeData.routes[rid];
        const oldPlacesArr = oldRoute.places;
        const n            = oldPlacesArr.length - 1;
        // The “original remaining to end” at oldPlacesArr[0]:
        // const totalDist = oldPlacesArr[0].dist;
        // const totalAsc  = oldPlacesArr[0].asc;
        // const totalDesc = oldPlacesArr[0].desc;

        // Build reversed array with inverted stats:
        const newPlacesArr = [];
        let newSplitIndex = null;
        for (let i = 0; i <= n; i++) {
            const oldP = oldPlacesArr[n - i];
            const newP = {
                id:     oldP.id,
                lat:    oldP.lat,
                lon:    oldP.lon,
                alt:    oldP.alt,
                // sluggy: oldP.sluggy,
                type:   oldP.type,
                asc: oldP.ascFromStart,
                ascFromStart: oldP.asc,
                desc: oldP.descFromStart,
                descFromStart: oldP.desc,
                dist: oldP.distFromStart,
                distFromStart: oldP.dist,
            };
            if(oldP.hasOwnProperty('shadowId')) {
                newP.shadowId = oldP.shadowId;
            }
            if (oldP.intersect) {
                newP.intersect = oldP.intersect;
                newP.childRouteId = oldP.childRouteId;

                // This each is running backwards, so we'll hit the old join first.
                if(oldP.intersectType === 'join') {
                    // Swap intersect type for reversed route.
                    newP.intersectType = 'split';
                    newSplitIndex = i;
                } else {
                    newP.intersectType = 'join';

                    if(newSplitIndex !== null) {
                        // Copy over values from old split to new split.
                        newPlacesArr[newSplitIndex].shadowDist = oldP.shadowDist;
                        newPlacesArr[newSplitIndex].shadowAsc = oldP.shadowDesc; // Swap shadow ascending and descending for reversed route.
                        newPlacesArr[newSplitIndex].shadowDesc = oldP.shadowAsc; // Swap shadow ascending and descending for reversed route.
                        newPlacesArr[newSplitIndex].childDistDiff = oldP.childDistDiff;
                    }
                    newSplitIndex = null;
                }
            }
            newPlacesArr.push(newP);
        }

        reversed.routes[rid] = {
        id:           oldRoute.id,
        text:         oldRoute.text,
        slug:         oldRoute.slug,
        description:  oldRoute.description,
        parent:       oldRoute.parent,
        color:        oldRoute.color,
        opacity:      oldRoute.opacity,
        totalDist:    oldRoute.totalDist,
        totalAsc:     oldRoute.totalDesc, // Swap ascending and descending for reversed route.
        totalDesc:    oldRoute.totalAsc, // Swap ascending and descending for reversed route.
        places:       newPlacesArr
        };
    });

    return reversed;
}

/**
 * Gets the place ID by slug from the place data.
 * @param {string} placeSlug - The slug of the place to find.
 * @return {string|false} The ID of the place if found, otherwise false.
 */
export function getPlaceIdBySlug(placeSlug) {
    const placeId = searchObject(placeData.places, 'slug', placeSlug);
    return placeId ? parseInt(placeId) : false;
}
/**
 * 
 * @param {string} placeSlug 
 * @returns {Object|false} The place object if found, otherwise false.
 */
export function getPlaceBySlug(placeSlug) {    
    const placeId = getPlaceIdBySlug(placeSlug);
    if (placeId) {
        return placeData.places[placeId];
    } else {
        log('Place not found for slug: ', placeSlug);
        return false;
    }
}

/**
 * Gets a place by its ID.
 * @param {string|number} placeId - The ID of the place to find.
 * @return {Object|false} The place object if found, otherwise false.
 */ 
export function getPlaceById(placeId) {
    return placeData.places[parseInt(placeId)] || false;
}

/**
 * Gets the index of the place along a route.
 * @param {string|number} routeId - The ID of the route to search in.
 * @param {string|number} placeId - The ID of the place to find.
 * @return {number|false} The index of the place along the route if found, otherwise false.
 * Note: Number could = 0 if place is first in route, so make sure to compare === false, not just if it is falsy.
 */
// TODO: THIS FUNCTION IGNORES DIRECTION!
// The one below it doesn't. Should I use it instead???
export function getPlaceIndexAlongRoute(routeId, placeId) {
    // Make sure routeId and placeId are numbers for consistency.
    routeId = parseInt(routeId);
    placeId = parseInt(placeId);

    // Check if the route exists in placeData
    if (!placeData.routes[routeId]) {
        log('Route not found for ID: ', routeId);
        return false;
    }

    // Get the places array for the specified route
    const places = placeData.routes[routeId].places;

    // Find the index of the placeId in the places array
    const index = places.findIndex(place => place.id === placeId);

    // Return the index if found, otherwise return false
    return index !== -1 ? index : false;
}

/**
 * 
 * @param {string|number} routeId - The ID of the route to search in.
 * @param {string} direction - The direction of the route. 'forward' or 'reverse'.
 * @param {string|number} placeId - The ID of the place to find.
 * @return {number|false} The index of the place along the route if found, otherwise false. (Number could = 0 if place is first in route, so make sure to compare === false, not just if it is falsy.)
 */
function getIndexOfPlaceAlongRoute(routeId, direction=null, placeId=null) {
    // Make sure routeId and placeId are numbers for consistency.
    routeId = parseInt(routeId);
    placeId = parseInt(placeId);

    // Get the working data.
    const workingData = (direction === 'reverse') ? getReversedPlaceData() : getAllPlaceData();

    // Validate routeId.
    if(!workingData.routes[routeId]) {
        log('Route not found: ', routeId);
        return false;
    }

    // Get the index of the place along the route.
    const index = workingData.routes[routeId].places.findIndex(pl => pl.id === placeId);
    if(index === -1) {
        log('Place not found in route: ', placeId);
        return false;
    }
    return index;
}

/**
 * Gets the ID of the next place along a route.
 * @param {string|number} routeId - The ID of the route to search in.
 * @param {string|number} placeId - The ID of the place to find.
 * @return {string|false} The ID of the next place if found, otherwise false.
 */
export function getNextPlaceIdAlongRoute(routeId, placeId, direction=null) {
    const placeIndex = getPlaceIndexAlongRoute(routeId, placeId);
    if(placeIndex !== false) {
        if(direction === 'reverse') {
            return placeData.routes[routeId].places[placeIndex - 1]?.id || false;
        } else {
            return placeData.routes[routeId].places[placeIndex + 1]?.id || false;
        }
    }
    return false;
}

/**
 * Gets all child routes of a route.
 * @param {string|number} routeId - The ID of the route to get child routes for.
 * @return {Array} The child routes.
 */
export function getChildRoutes(routeId) {
    // Make sure routeId is a number for consistency.
    routeId = parseInt(routeId);
    
    // Get all child routes in the route.
    let childRoutes = [];
    for (const potentialChildRouteId in placeData.routes) {
        if (placeData.routes[potentialChildRouteId].hasOwnProperty('parent') && placeData.routes[potentialChildRouteId].parent === routeId) {
            childRoutes.push(potentialChildRouteId);
        }
    }
    return childRoutes;
}


/**
 * Gets the child route a place is along.
 * @param {string|number} routeId - The ID of the parent route to check.
 * @param {string|number} placeId - The ID of the place to check.
 * @return {int|false} The ID of the child route if the place is along a child route of the route, otherwise false.
 */
export function getChildRoutePlaceIsAlong(routeId, placeId) {
    const childRoutes = getChildRoutes(routeId);
    for (const childRouteId of childRoutes) {
        if (getPlaceIndexAlongRoute(childRouteId, placeId) !== false) {
            return parseInt(childRouteId);
        }
    }
    return false;
}

/**
 * Gets a place along a route by its ID.
 * @param {string|number} routeId - The ID of the route to search in.
 * @param {string|number} placeId - The ID of the place to find.
 * @param {boolean} searchChildRoutes - Whether to search child routes if the place is not found along the primary route.
 * @return {Object|false} The place object if found, otherwise false.
 */ 
export function getPlaceAlongRoute(routeId, placeId, searchChildRoutes = false) {
    // Make sure routeId and placeId are numbers for consistency.
    routeId = parseInt(routeId);
    placeId = parseInt(placeId);

    // Check if the route exists in placeData
    const placeIndexAlongRoute = getPlaceIndexAlongRoute(routeId, placeId);

    if (placeIndexAlongRoute === false) {
        // Place not found along primary route, check child routes if allowed.
        if(searchChildRoutes) {
            const childRouteId = getChildRoutePlaceIsAlong(routeId, placeId);
            if(childRouteId !== false) {
                return getPlaceAlongRoute(childRouteId, placeId, false);
            }
        }
        return false;
    }

    // Return the place object if found
    return placeData.routes[routeId].places[placeIndexAlongRoute];
}

/**
 * Gets a route by its ID.
 * @param {string|number} routeId - The ID of the route to find.
 * @return {Object|false} The route object if found, otherwise false.
 */ 
export function getRouteById(routeId) {
    return placeData.routes[parseInt(routeId)] || false;
}

/**
 * Gets the route ID by slug from the place data.
 * @param {string} routeSlug - The slug of the route to find.
 * @return {string|false} The ID of the route if found, otherwise false.
 */ 
export function getRouteIdBySlug(routeSlug) {
    const routeId = searchObject(placeData.routes, 'slug', routeSlug);
    return routeId ? parseInt(routeId) : false;
}

/**
 * Gets the route ID by slug from the appConfig primaryRoutes lookup table.
 * For use when placeData is not yet loaded.
 * @param {string} routeSlug - The slug of the route to find.
 * @return {string|false} The ID of the route if found, otherwise false.
 */ 
export function getRouteIdBySlug_appConfigLookup(routeSlug) {
    for(const routeId in window.ironman.appConfig.primaryRoutes) {
        if(window.ironman.appConfig.primaryRoutes[routeId].slug === routeSlug) {
            return parseInt(routeId);
        }
    }
    return false;
}

/**
 * Gets a route by its slug.
 * @param {string} routeSlug 
 * @returns {Object|false} The route object if found, otherwise false.
 */
export function getRouteBySlug(routeSlug) {
    const routeId = getRouteIdBySlug(routeSlug);
    if (routeId) {
        return placeData.routes[routeId];
    } else {
        log('Route not found for slug: ', routeSlug);
        return false; // Return false if no route found with the given slug.
    }
}

/**
 * Validates the path parameters from user inputs and returns a valid params object.
 * BASICALLY, IT CONVERTS SLUGS TO IDS AND THEN PASSES TO validatePathIds().
 * 
 * @param {Object} userInputs - The user inputs object containing route, direction, origin, destination, and via.
 * NOTE: Expects via as comma separated string of slugs.
 * @param {boolean} returnSlugs - Whether to return the slugs instead of the ids.
 * @return {Object} params - The validated parameters object containing route, direction, origin, destination, and via.
 * If any of the parameters are invalid, they will be set to null or an empty array.
 */ 
export function validatePathSlugs(userInputs, returnSlugs = true) {
    log('Validating pathSlugs: ', userInputs);
    // Convert all slugs to ids.
    const pathIds = {
        route: hasKey(userInputs,'route') && getValue(userInputs,'route') ? getRouteIdBySlug(normalizeString(getValue(userInputs,'route'))) : null,
        direction: hasKey(userInputs,'direction') && getValue(userInputs,'direction') && (getValue(userInputs,'direction') === 'reverse') ? 'r' : 'f',
        origin: hasKey(userInputs,'origin') && getValue(userInputs,'origin') ? getPlaceIdBySlug(normalizeString(getValue(userInputs,'origin'))) : null,
        destination: hasKey(userInputs,'destination') && getValue(userInputs,'destination') ? getPlaceIdBySlug(normalizeString(getValue(userInputs,'destination'))) : null,
        via: null
    };

    // Loop through the via slugs and get the ids.
    if(hasKey(userInputs,'via') && getValue(userInputs,'via') && getValue(userInputs,'via').length > 0) {
        log('Validating via slugs: ', getValue(userInputs,'via'));
        const viaIds = [];
        const viaSlugs = getValue(userInputs,'via').split(',');
        for (const slug of viaSlugs) {
            const routeId = getRouteIdBySlug(normalizeString(slug));
            if (routeId !== false) {
                viaIds.push(routeId);
            }
        }
        log('Validated via ids: ', viaIds);
        if(viaIds.length > 0) {
            pathIds.via = viaIds.join(',');
        }
    }

    log('Validating pathIds: ', pathIds);

    // Validate the path ids.
    return validatePathIds(pathIds, returnSlugs);
}

/**
 * Validates the path parameters from user inputs and returns a valid params object.
 * @param {Object} userInputs - The user inputs object containing route, direction, origin, destination, and via.
 * @param {boolean} returnSlugs - Whether to return the slugs instead of the ids.
 * @return {Object} params - The validated parameters object containing route, direction, origin, destination, via, and viaArray. ALL OTHER PARAMS WILL IGNORED.
 * If any of the parameters are invalid, they will be set to null (or an empty array for viaArray).
 * 
 * NOTE: Expects via as comma separated string of ids. OR a single id as a string or number.
 * Example userInputs object:
 * {
 *     route: '50',
 *     direction: 'r', // Treats anything else as 'forward'
 *     origin: '1509',
 *     destination: '1646',
 *     via: 88 OR '88' OR '88,89'
 * }
 * Example return object:
 * {
 *     route: 50,
 *     direction: 'f',
 *     origin: 1509,
 *     destination: 1646,
 *     via: '88' OR '88,89'
 *     viaArray: [88] OR [88,89]
 * }
 * Example return object with returnSlugs = true:
 * {
 *     route: 'french-way',
 *     direction: 'forward',
 *     origin: 'roncesvalles',
 *     destination: 'ponferrada',
 *     via: 'child-route-slug-1,child-route-slug-2',
 *     viaArray: ['child-route-slug-1','child-route-slug-2']
 * }
 */ 
export function validatePathIds(userInputs, returnSlugs = false) {
    let p = userInputs;
    
    // Initialize the param objects for output.
    let paramIds = {
        route: null,
        direction: 'f', // Default direction
        origin: null,
        destination: null,
        via: null,
        viaArray: []
    };
    let paramSlugs = {
        route: null,
        direction: 'forward', // Default direction
        origin: null,
        destination: null,
        via: null,
        viaArray: []
    };

    // Validate the route.
    if (hasKey(p,'route') && getValue(p,'route')) {
        // Validate the route slug against the available routes in placeData
        let route = getRouteById(getValue(p,'route'));
        if ((route !== false) && !hasKey(route,'parent')) {
            log('Route is valid primary route.');
            // Route is valid primary route.
            paramIds.route = route.id; // Set to the valid id
            paramSlugs.route = route.slug; // Set to the valid slug

            // Validate the direction.
            let useDirection = 'forward'
            if(hasKey(p,'direction') && getValue(p,'direction') === 'r') {
                paramIds.direction = 'r';
                paramSlugs.direction = 'reverse';
                useDirection = 'reverse';
            }

            // Validate the origin.
            if (hasKey(p,'origin') && getValue(p,'origin')) {
                // Validate the origin against the available places in placeData
                let originPlace = getPlaceById(getValue(p,'origin'));
                log('Origin Place: ', originPlace);
                if ((originPlace !== false) && (originPlace.type !== 'intersection')) {
                    // Check if origin is on Primary Route
                    let originIndex = getPlaceIndexAlongRoute(route.id, originPlace.id);
                    let originIndexChild = false;
                    
                    // If origin is NOT on primary route, check child routes
                    let originChildRouteId = false;
                    if(originIndex === false) {
                        originChildRouteId = getChildRoutePlaceIsAlong(route.id, originPlace.id);
                        if(originChildRouteId !== false) {
                            // Get the place index along the child route
                            originIndexChild = getPlaceIndexAlongRoute(originChildRouteId, originPlace.id);
                            log('Origin Index Child: ', originIndexChild);
                        }
                    }

                    if(originIndex !== false || originIndexChild !== false) {
                        // Origin is along provided route or child route of provided route.
                        paramIds.origin = originPlace.id; // Set to the valid id
                        paramSlugs.origin = originPlace.slug; // Set to the valid slug
                
                        // Validate the destination.
                        if (hasKey(p,'destination') && getValue(p,'destination')) {
                            // Validate the destination against the available places in placeData
                            let destinationPlace = getPlaceById(getValue(p,'destination'));
                            log('Destination Place: ', destinationPlace);
                            if ((destinationPlace !== false) && (destinationPlace.type !== 'intersection')) {
                                // Destination is valid place...

                                // Check if destination is along same route as origin
                                let destinationIndex = getPlaceIndexAlongRoute(route.id, destinationPlace.id);
                                let destinationIndexChild = false;

                                // If destination is not on primary route, check child routes
                                let destinationChildRouteId = false;
                                if(destinationIndex === false) {
                                    destinationChildRouteId = getChildRoutePlaceIsAlong(route.id, destinationPlace.id);
                                    if(destinationChildRouteId !== false) {
                                        // Get the place index along the child route
                                        destinationIndexChild = getPlaceIndexAlongRoute(destinationChildRouteId, destinationPlace.id);
                                        log('Destination Index Child: ', destinationIndexChild);
                                    }
                                }
        
                                if (destinationIndex !== false || destinationIndexChild !== false) {
                                    // Destination is along primary route or child route of primary route

                                    // Now check that destination is reachable from origin.
                                    let engagePathfinder = false;

                                    // If both origin and destination are along primary route, check if destination is after origin.
                                    if(originIndex !== false && destinationIndex !== false) {
                                        if((useDirection === 'forward' && destinationIndex > originIndex) || 
                                            (useDirection === 'reverse' && destinationIndex < originIndex)
                                        ) {
                                            // Destination is after origin, so it is valid.
                                            paramIds.destination = destinationPlace.id; // Set to the valid id
                                            paramSlugs.destination = destinationPlace.slug; // Set to the valid slug
                                            log('Destination is valid: ', paramSlugs.destination);

                                            // If via slugs were provided, we need pathfinder to validate them.
                                            if(hasKey(p,'via') && getValue(p,'via') && getValue(p,'via').length > 0) {
                                                log('Engaging pathfinder to validate via slugs: ', getValue(p,'via'));
                                                engagePathfinder = true;
                                            }
                                        }
                                    } else if(
                                        originChildRouteId !== false && 
                                        destinationChildRouteId !== false && 
                                        originIndexChild !== false && 
                                        destinationIndexChild !== false && 
                                        originChildRouteId === destinationChildRouteId
                                    ) {
                                        // Both origin and destination are along the same child route of the primary route.
                                        log('Both origin and destination are along the same child route. No via allowed as no nested child routes.');
                                        // Check if destination is reachable from origin.
                                        if((useDirection === 'forward' && destinationIndexChild > originIndexChild) || 
                                            (useDirection === 'reverse' && destinationIndexChild < originIndexChild)
                                        ) {
                                            // Destination is after origin, so it is valid.
                                            paramIds.destination = destinationPlace.id; // Set to the valid id
                                            paramSlugs.destination = destinationPlace.slug; // Set to the valid slug

                                            // No via slugs allowed if origin and destination are on same child route.
                                        }
                                    } else {
                                        // Origin and destination are not along the same route or child route.
                                        // Engage pathfinder to confirm destination is reachable from origin.
                                        // We have already confimed that origin and destination ARE places along the route or child route, we just need to confirm destination is reachable from origin.
                                        log('Origin and destination are not along the same route or child route. Engaging pathfinder to confirm destination is reachable from origin.');
                                        engagePathfinder = true;
                                    }

                                    // If engagePathfinder is true, engage pathfinder to confirm destination is reachable from origin and validate via input.
                                    if(engagePathfinder) {
                                        const pathSegments = pathfinder(route.id, useDirection, originPlace.id, destinationPlace.id, []);
                                        log('Pathfinder Engaged (without via): ', pathSegments);
                                        if(pathSegments !== false) {
                                            // Destination is reachable from origin, so it is valid.
                                            paramIds.destination = destinationPlace.id; // Set to the valid id
                                            paramSlugs.destination = destinationPlace.slug; // Set to the valid slug

                                            // Validate via input based on pathSegments.
                                            if(hasKey(p,'via') && getValue(p,'via')) {
                                                const UserVia = getValue(p,'via');

                                                // Normalize the via input to an array of ids.
                                                let inputVia = [];
                                                if(typeof UserVia === 'string' && UserVia.length > 0) {
                                                    inputVia = UserVia.split(',').filter(Boolean);
                                                } else if (typeof UserVia === 'number') {
                                                    inputVia = [UserVia];
                                                }

                                                // Validate via input based on pathSegments.
                                                const outputViaIds = [];
                                                if(inputVia.length > 0) {                                                    
                                                    // Get all valid via routes from pathSegments.
                                                    let validViaRouteIds = [];
                                                    //loop through pathSegments and add all OPTIONAL child routeIds to validViaRoutes.
                                                    for(const pathSegment of pathSegments) {
                                                        if(pathSegment.routeId !== route.id && !pathSegment.onPath) {
                                                            validViaRouteIds.push(pathSegment.routeId);
                                                        }
                                                    }

                                                    // Loop through via input array and validate each viaId against validViaRoutes.
                                                    for(let viaRouteId of inputVia) {
                                                        log('Via Route ID: ', viaRouteId);
                                                        viaRouteId = parseInt(viaRouteId);
                                                        if(validViaRouteIds.includes(viaRouteId)) {
                                                            // ViaRouteId is valid.
                                                            outputViaIds.push(viaRouteId);
                                                            log('Valid viaRouteId found: ', viaRouteId);
                                                        }
                                                    }
                                                }
                                                // Prepare output via ids and slugs.
                                                if(outputViaIds.length > 0) {
                                                    paramIds.viaArray = outputViaIds;
                                                    paramIds.via = outputViaIds.join(',');
                                                    for(const viaRouteId of outputViaIds) {
                                                        paramSlugs.viaArray.push(getRouteById(viaRouteId).slug);
                                                    }
                                                    paramSlugs.via = paramSlugs.viaArray.join(',');
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    // Return the param objects based on returnSlugs.
    if(returnSlugs) {
        return paramSlugs;
    } else {
        return paramIds;
    }
}

/**
 * Converts a path object to a normalized query string.
 * @param {Object} params - The path parameters.
 * @returns {string} The query string.
 */
export function getPathQueryString(params) {
    let url = '';
    if(params.route) {
        url += '?route=' + params.route;
        if(params.direction === 'reverse') {
            url += '&direction=reverse';
        }
        if(params.origin) {
            url += '&origin=' + params.origin;
            if(params.destination) {
                url += '&destination=' + params.destination;
                if(params.via) {
                    url += '&via=' + params.via;
                }
            }    
        }
    }
    return url;
}

/**
 * Calculates the path data for a given path.
 * @param {Object} pathSegments - The path segments from pathfinder to calculate the data for.
 * @returns {Object} The path data.
 */
export function calculatePathData(pathSegments) {
    
    // Initialize the path data object.
    let pathData = {
        distance: 0,
        ascent: 0,
        descent: 0,
        selectedVias: [],
        unselectedVias: [],
        requiredVias: []
    };

    // Loop through the path segments
    for(const pathSegment of pathSegments) {

        // calculate the distance, ascent, and descent.
        if(pathSegment.onPath) {
            pathData.distance += pathSegment.distance;
            pathData.ascent += pathSegment.ascent;
            pathData.descent += pathSegment.descent;
        }

        // Add vias to the path data.
        if(pathSegment.isChildRoute) {
            if(pathSegment.onPath) {
                if(pathSegment.isRequired) {
                    pathData.requiredVias.push(pathSegment.routeId);
                } else {
                    pathData.selectedVias.push(pathSegment.routeId);
                }
            } else {
                pathData.unselectedVias.push(pathSegment.routeId);
            }
        }
    }

    return pathData;
}

/**
 * Fetches API data from the API, var, or from local cache and saves it ENCRYPTED to the fetchedAPIData variable.
 * 
 * @param {string|null} routeSlug - The slug of the route to fetch. (Can be null for listings api if placeId is provided.)
 * @param {string, either 'mapserver', 'listings', 'places', or 'friends'} apiName - The name of the API to get the data for.
 * @param {integer|null} placeId - The ID of the place to fetch the data for. (Only for listings api.)
 * @returns {Promise} A promise that resolves with the API data. (Or rejects with an error if the API data cannot be fetched.)
 */
export function fetchAPIData(apiName, routeSlug=null, inputPlaceId=null, spinnerType=null, spinnerMessage=null) {
    // Return error if apiName is not valid.
    const validApiNames = ['mapserver', 'listings', 'places', 'friends'];
    if(!validApiNames.includes(apiName)) {
        return Promise.reject(new Error('Invalid apiName parameter: ' + apiName));
    }
    
    let mixedCacheKey = null;
    let routeId = null;
    let placeId = null;

    // Validate the input parameters based on the apiName.
    if(apiName === 'listings') {
        // Listings API requires either routeSlug or placeId.
        if(routeSlug && typeof routeSlug === 'string' && routeSlug.trim() !== '') {
            // Normalize the routeSlug to ensure consistent casing
            routeSlug = routeSlug.toLowerCase().trim();
            // Validate using appConfig route lookup table instead of placeData that maybe not yet loaded.
            // routeId = getRouteIdBySlug(routeSlug); // This is the old way, but placeData may not be loaded yet.
            routeId = getRouteIdBySlug_appConfigLookup(routeSlug);
            if (routeId) {
                mixedCacheKey = 'route:'+routeSlug;
            } else {
                return Promise.reject(new Error('Route not found for slug: ' + routeSlug));
            }
        } else if(inputPlaceId) {
            placeId = parseInt(inputPlaceId);
            // Validate the placeId.
            if(!placeId) {
                return Promise.reject(new Error('Invalid placeId parameter: ' + inputPlaceId));
            }
            mixedCacheKey = 'place:'+placeId;
        } else {
            return Promise.reject(new Error('Invalid routeSlug or placeId parameter: ' + routeSlug + ' or ' + placeId));
        }
    } else if(apiName === 'mapserver') {
        // Mapserver API requires routeSlug or "all".
        if(routeSlug && routeSlug.trim() === 'all') {
            mixedCacheKey = 'route:all'; 
            routeId = 'all';
        } else if(routeSlug && typeof routeSlug === 'string' && routeSlug.trim() !== '') {
            // Normalize the routeSlug to ensure consistent casing
            routeSlug = routeSlug.toLowerCase().trim();
            // Validate using appConfig route lookup table instead of placeData that maybe not yet loaded.
            // routeId = getRouteIdBySlug(routeSlug); // This is the old way, but placeData may not be loaded yet.
            routeId = getRouteIdBySlug_appConfigLookup(routeSlug);
            if (routeId) {
                mixedCacheKey = 'route:'+routeSlug;
            } else {
                return Promise.reject(new Error('Route not found for slug: ' + routeSlug));
            }
        } else {
            return Promise.reject(new Error('Invalid routeSlug or placeId parameter: ' + routeSlug + ' or ' + placeId));
        }
    } else if(apiName === 'places') {
        // Places API requires no parameters.
        mixedCacheKey = 'all';
        routeId = 'all'; //Just a placeholder for the url parameter.
    } else if(apiName === 'friends') {
        // Friends API requires no parameters.
        mixedCacheKey = 'all';
        routeId = 'all'; //Just a placeholder for the url parameter.
    } else {
        return Promise.reject(new Error('Invalid apiName parameter: ' + apiName));
    }
    
    if(mixedCacheKey) {
        // Check if the data is already fetched and cached by mixedId.
        if (fetchedAPIData[apiName][mixedCacheKey]) {
            log('API data for route already cached: ', mixedCacheKey);
            // Return the cached API data
            return Promise.resolve(fetchedAPIData[apiName][mixedCacheKey]);
        } else if(inProgressAPIDownloadPromises[apiName][mixedCacheKey]) {
            log('API data for route already in progress: ', mixedCacheKey);
            // Return the cached in-progress API download promise
            return inProgressAPIDownloadPromises[apiName][mixedCacheKey];
        } else {
            // TODO: Implement localStorage cache. (not for friends api)

            // Return a promise from fetchAPIDataFromAPI() that resolves with the fetched API data
            const downloadPromise = fetchAPIDataFromAPI(apiName, routeId, placeId, spinnerType, spinnerMessage).then(function (encryptedAPIData) {

                // TODO: Save encrypted API data to localStorage here.

                // Save the fetched API data to the global variable
                fetchedAPIData[apiName][mixedCacheKey] = encryptedAPIData.data;

                // Remove the in-progress API download promise from the global variable
                delete inProgressAPIDownloadPromises[apiName][mixedCacheKey];

                // No return, just set the module fetchedAPIData variable.
            });

            // Save the in-progress API download promise to the global variable
            inProgressAPIDownloadPromises[apiName][mixedCacheKey] = downloadPromise;

            // Return the in-progress API download promise
            return downloadPromise;
        }
    } else {
        return Promise.reject(new Error('Invalid input parameters: ' + routeSlug + ' or ' + apiName + ' or ' + placeId));
    }
}

/**
 * Fetches API data for a specific route from the API.
* @param {string, can be places, listings, mapserver, friends, etc.} apiName - The name of the API to get the data for. 
* @param {integer} routeId - The ID of the route to fetch.
* @param {integer|null} placeId - The ID of the place to fetch the data for. (Only for listings api.)
 * @returns {Promise} A promise that resolves with the API data.
 */
function fetchAPIDataFromAPI(apiName, routeId=null, placeId=null, spinnerType=null, spinnerMessage=null) {
    // Convert apiName to the corresponding letter.
    const apiLetter = apiName.charAt(0);
    let apiUrl = null;
    let spinnerSlug = 'fetchAPIDataFromAPI';

    // Set API Url with routeId or placeId and last updated
    if(routeId) {
        apiUrl = apiConfig[apiLetter].endpoint + '&r=' + routeId + '&last_updated=' + apiConfig[apiLetter].lastUpdated;
        spinnerSlug += '-' + apiName + '-route:'+routeId;
    } else if(placeId) {
        apiUrl = apiConfig[apiLetter].endpoint + '&place_id=' + placeId + '&last_updated=' + apiConfig[apiLetter].lastUpdated;
        spinnerSlug += '-' + apiName + '-place:'+placeId;
    } else {
        return Promise.reject(new Error('Invalid routeId or placeId parameter: ' + routeId + ' or ' + placeId));
    }

    // For Places API, append users UI language to the API request
    if(apiName === 'places' && window.ironman.userData.userLanguage) {
        const userLanguage = window.ironman.userData.userLanguage;
        // const userLanguage = 'fr'; //testing
        apiUrl = apiUrl.concat('&lang=',userLanguage);
    }

    // Show the loading spinner.
    if(spinnerType) {
        updateLoadingSpinner(spinnerSlug, true, spinnerType, spinnerMessage);
    }

    // Return a jQuery AJAX promise to fetch the API data
    return $.ajax({
        method: 'GET',
        dataType: "json",
        url: apiUrl
    }).then(function (response) {
        // log('fetchAPIDataFromAPI: ', response);

        // Hide the loading spinner.
        if(spinnerType) {
            updateLoadingSpinner(spinnerSlug, false, spinnerType);
        }

        return response; // This now gets passed to .then()
    }).fail(function (xhr, status, error) {
        log('Error fetching API data: ', status, error);
        if(spinnerType) {
            updateLoadingSpinner(spinnerSlug, 'failed', spinnerType, 'Could not connect to the server.');
        }
        // Handle error appropriately, maybe show an alert or log it.
        return {}; // Return empty object on error to avoid breaking the app.
    });
}

/**
 * Returns a promise that resolves with a frozen copy of the fetched, decrypted API data.
* @param {string} apiName - The name of the API to get the data for. 
* @param {string} mixedCacheKey - The mixed cache key of the API data to get the data for.
 * @returns {Object} A promise that resolves with the FROZEN decrypted API data.
 */
export function getFetchedAPIData(apiName, routeSlug=null, placeId=null) {
    // Assemble the mixed cache key.
    let mixedCacheKey = 'all';
    if(routeSlug) {
        mixedCacheKey = 'route:'+routeSlug;
    } else if(placeId) {
        mixedCacheKey = 'place:'+placeId;
    }
    if (!fetchedAPIData[apiName][mixedCacheKey]) throw new Error('API data not fetched for ' + apiName + ' with mixedCacheKey: ' + mixedCacheKey);
    
    // Note: Returns a promise that resolves with the FROZEN decrypted API data.
    return decryptAPIData(fetchedAPIData[apiName][mixedCacheKey], 'ironman_api_passphrase').then(function (decryptedAPIData) {
        // Process or return the API data based on the apiName.
        if(apiName === 'places') {
            // No return, just set the module placeData variable.
            placeData = deepFreeze(enrichPlaceData(decryptedAPIData));
            log('Place data fetched and enriched successfully.', placeData);
        } else {
            // All the other APIs get returned (with or without enrichment)
            if(apiName === 'listings') {
                // Enrich the listings data before returning it.
                decryptedAPIData = enrichListingsData(decryptedAPIData);
            } else if(apiName === 'friends') {
                // Enrich the friends data before returning it.
                decryptedAPIData = enrichFriendsData(decryptedAPIData);
            }
            // Return the decrypted API data.
            return deepFreeze(decryptedAPIData);
        }
    });
}

/**
 * Enriches the place data and returns it.
 * @param {Object} inputPlaceData - The place data to enrich.
 * @returns {Object} The enriched place data.
 */
function enrichPlaceData(inputPlaceData) {
    /*
    // Add helpful properties to each place in each route.
    
    // Create a lookup table of route slugs to route ids.
    const route_lookup = {};
    $.each(inputPlaceData.routes, function (routeId, route) {
        route_lookup[route.slug] = parseInt(routeId);
    });

    // Loop through each route and enrich the places.
    $.each(inputPlaceData.routes, function (routeId, route) {
        let index = 0;
        let maxDistance = 0;
        let maxAscending = 0;
        let maxDescending = 0;

        let shadowRouteId = null;
        $.each(route.places, function (placeId, place) {
            if(index === 0) {
                maxDistance = place.dist;
                maxAscending = place.asc;
                maxDescending = place.desc;
            }
            place.distFromRouteStart = maxDistance - place.dist;
            place.ascFromRouteStart = maxAscending - place.asc;
            place.descFromRouteStart = maxDescending - place.desc;

            // Enrich intersection data.
            if(place.hasOwnProperty('intersect')) {
                // See if this place is a split or join.
                const intersectId = route_lookup[place.intersect] || false;
                if(!intersectId) {
                    log('Intersect route not found: ', place.intersect);
                    return false;
                }

                if(intersectId && intersectId === shadowRouteId) {
                    // This is a join.
                    place.intersectTypeX = 'join';
                    place.intersectRouteId = intersectId;
                    // End shadowing the child route.
                    shadowRouteId = null;
                } else {
                    // This is a split.
                    place.intersectTypeX = 'split';
                    place.intersectRouteId = intersectId;
                    // Start shadowing the child route.
                    shadowRouteId = intersectId;
                }
            } else {
                if(shadowRouteId) {
                    place.shadowRouteId = shadowRouteId;
                }
            }

            index++;
        });
    });
    */
    return inputPlaceData;
}

/**
 * Enriches the listings data and returns it.
 * @param {Object} inputListingsData - The listings data to enrich.
 * @returns {Object} The enriched listings data.
 */
function enrichListingsData(inputListingsData) {
    // Create a 2nd object inside inputListingsData (listings_by_place) which lists the listing ids by parent_place_id.
    inputListingsData.listings_by_place = {};
    $.each(inputListingsData.listings, function (listingId, listing) {
        if(!inputListingsData.listings_by_place[listing.parent_place_id]) {
            inputListingsData.listings_by_place[listing.parent_place_id] = [];
        }
        inputListingsData.listings_by_place[listing.parent_place_id].push(listingId);
    });
    return inputListingsData;
}

function enrichFriendsData(inputFriendsData) {
    // Create a 2nd object inside inputFriendsData (friends_by_place) which lists the friend ids by checkinPlaceId.
    inputFriendsData.friends_by_place = {};
    $.each(inputFriendsData.friends, function (friendId, friend) {
        if(!inputFriendsData.friends_by_place[friend.checkinPlaceId]) {
            inputFriendsData.friends_by_place[friend.checkinPlaceId] = [];
        }
        inputFriendsData.friends_by_place[friend.checkinPlaceId].push(friendId);
    });
    return inputFriendsData;
}

/**
 * Gets the allowed markers from the appConfig.
 * @returns {Object} The allowed markers.
 */
export function getAllowedMarkers() {
    let allowedMarkers = {};
    allowedMarkers['places'] = Object.keys(window.ironman.appConfig.placeTypes.places.children);
    let serviceCats = Object.keys(window.ironman.appConfig.servicesOffered);
    // Loop through allowedMarkers and add the children of each service to the allowedMarkers array.
    serviceCats.forEach(function(service) {
        allowedMarkers[service] = Object.keys(window.ironman.appConfig.servicesOffered[service].children);
    });
    allowedMarkers['friends'] = [];
    return allowedMarkers;
}

/**
 * Initializes the range sliders in the given scope.
 * @param {Element} scope - The scope to initialize the range sliders in.
 * @requires https://refreshless.com/nouislider/
 */
export function initRangeSliders(scope = document) {
    log('initRangeSliders', scope);

    const rangeSliderWidgets = scope.querySelectorAll('[data-range-slider]');
    log('rangeSliderWidgets', rangeSliderWidgets);

    if (rangeSliderWidgets.length === 0) {
        log('No range slider widgets found in scope: ', scope);
        return;
    }

    const MPH_TO_KMH = 1.609344;

    const htmlElement = document.documentElement;
    const direction =
        htmlElement.getAttribute('dir') === 'ltr' ||
        htmlElement.getAttribute('dir') === 'rtl'
            ? htmlElement.getAttribute('dir')
            : 'ltr';

    rangeSliderWidgets.forEach((rangeSliderWidget) => {
        const rangeSlider = rangeSliderWidget.querySelector('.range-slider-ui');

        if (!rangeSlider || rangeSlider.noUiSlider) {
            log('Range slider doesnt exist or is already initialized: ', rangeSliderWidget);
            return;
        }

        const valueInput = rangeSliderWidget.querySelector('[data-label="range-slider-value"]');

        // These values are in the DISPLAY unit.
        // If units="mi", they mean mph.
        // Otherwise, they mean km/h.
        const minDisplay = Number(rangeSliderWidget.dataset.rangeSliderMin ?? 1);
        const maxDisplay = Number(rangeSliderWidget.dataset.rangeSliderMax ?? 20);
        const startDisplay = Number(rangeSliderWidget.dataset.rangeSliderStart ?? minDisplay);
        const stepDisplay = Number(rangeSliderWidget.dataset.rangeSliderStep ?? 0.5);

        const units =
            rangeSliderWidget.dataset.rangeSliderUnits === 'mi'
                ? 'mi'
                : 'km';

        const isMiles = units === 'mi';
        const displayUnit = isMiles ? 'mph' : 'km/h';

        const toInternalKmh = (displayValue) => {
            return isMiles
                ? displayValue * MPH_TO_KMH
                : displayValue;
        };

        const formatDisplay = (value) => {
            return Number(value).toLocaleString(undefined, {
                maximumFractionDigits: 1,
            });
        };

        const formatInternal = (value) => {
            return Number(value).toFixed(4);
        };

        noUiSlider.create(rangeSlider, {
            direction,
            start: startDisplay,
            connect: 'lower',
            step: stepDisplay,
            pips: {
                mode: 'values',
                values: Array.from(
                    {
                        length: Math.floor(maxDisplay) - Math.ceil(minDisplay) + 1,
                    },
                    (_, i) => Math.ceil(minDisplay) + i
                ),
                density: 100,
            },
            tooltips: {
                to: (value) => `${formatDisplay(value)} ${displayUnit}`,
            },
            range: {
                min: minDisplay,
                max: maxDisplay,
            },
        });

        // Slider display value -> hidden/internal km/h value
        rangeSlider.noUiSlider.on('update', (values) => {
            if (!valueInput) return;

            const displayValue = Number(values[0]);
            const internalKmhValue = toInternalKmh(displayValue);

            valueInput.value = formatInternal(internalKmhValue);
        });

        // Hidden/internal km/h value -> slider display value
        // if (valueInput) {
        //     valueInput.addEventListener('change', function () {
        //         const internalKmhValue = Number(this.value);

        //         const displayValue = isMiles
        //             ? internalKmhValue / MPH_TO_KMH
        //             : internalKmhValue;

        //         rangeSlider.noUiSlider.set(displayValue);
        //     });
        // }
    });
}

/**
 * Destroys the range sliders in the given scope.
 * @param {Element} scope - The scope to destroy the range sliders in.
 * @requires https://refreshless.com/nouislider/
 */
export function destroyRangeSliders(scope = document) {
    log('destroyRangeSliders', scope);
    log(noUiSlider);

    const rangeSliderWidgets = scope.querySelectorAll('[data-range-slider]')
    log('rangeSliderWidgets', rangeSliderWidgets);
    if (rangeSliderWidgets.length === 0) {
        log('No range slider widgets found in scope: ', scope);
        return;
    }
    
    rangeSliderWidgets.forEach((rangeSliderWidget) => {
        const rangeSlider = rangeSliderWidget.querySelector('.range-slider-ui')
        if (!rangeSlider || !rangeSlider.noUiSlider) {
            log('Range slider doesnt exist or is not initialized: ', rangeSliderWidget);
            return;
        }
        rangeSlider.noUiSlider.destroy();
    });
}

export async function showRouteSelectModal(stageId=null, selectedRouteSlug=null, selectedDirection=null) {
    log('showRouteSelectModal', stageId, selectedRouteSlug, selectedDirection);

    // Make sure the Fenix modal has a backdrop.
    await setFenixBackdrop(true);

    // Update the Fenix modal with the Route Select modal content.
    const modalTitle = t('selectRoute');
    const modalFooter = getTemplate('fenixDefaultFooterTemplate'); // Use the default footer template and customize it.
    const modalDesktopSize = 'md';
    const modalMobileHeight = null; //Use default height.
    const modalDataPurpose = 'routeSelect'; // To ID this modal content for JS/CSS selectors. This is used to identify the modal content for JS/CSS selectors.

    // Customize the default modal footer...
    modalFooter.find('[data-label="fenixDefaultFooterSaveButton"]').text(t('selectRoute')).attr('data-action', 'routeSelectModal_submit').attr('data-stage-id', stageId);
    
    // Load animated bootstrap placeholder for loading.
    setFenixModalContent(modalTitle, getTemplate('loadingPlaceholder'), modalFooter, modalDesktopSize, modalMobileHeight, modalDataPurpose);
    
    await openFenixModal();
    
    await nextFrame(); //wait for the next frame to ensure the modal is open before continuing.
    await delay(100); //wait N milliseconds for testing.

    let modalBody = getTemplate('routeSelectModalTemplate');
    // Start customizing the modal body...
    
    // Get all primary routes from global object
    const primaryRoutes = window.ironman.appConfig.primaryRoutes || [];
    log('primaryRoutes', primaryRoutes);

    const routeItemList = [];
    // Loop through primary routes and add them to the modal body
    $.each(primaryRoutes, function(index, route) {
        const routeItem = getTemplate('routeSelectSingleItemTemplate').clone();
        routeItem.attr('data-route-id', route.id);
        routeItem.find('input[data-label="radio-select"]').attr('value', route.id);

        routeItem.find('[data-label="routeIcon"]').attr('src', route.icon).attr('alt', route.name);
        routeItem.find('[data-label="route-name-link"]').text(route.name).attr('href', '/camino/'+route.slug+'/');
        // routeItem.find('[data-label="route-type"]').text(route.type);
        routeItem.find('[data-label="route-distance"]').text(displayLongDistance(route.start?.distance_to_destination || 0));

        routeItem.find('[data-label="route-start-location"]').attr('href', '/places/'+route.start?.slug+'/' || '#').text(route.start?.name || '');
        routeItem.find('[data-label="route-finish-location"]').attr('href', '/places/'+route.finish?.slug+'/' || '#').text(route.finish?.name || '');
        
        // Set the selected route and direction in the modal.
        if(selectedRouteSlug === route.slug) {
            routeItem.find('input[data-label="radio-select"]').attr('checked', 'checked');
        }
        
        // Add the route item <li> to the <ul>
        routeItemList.push(routeItem);
    });

    modalBody.find('ul[data-label="route-list-container"]').empty().append(routeItemList);

    if(selectedDirection === 'reverse') {
        modalBody.find('[data-label="reverse-direction-input"]').attr('checked', 'checked');
    }

    // End customizing the modal body...

    // Set the modal content. True means don't change it - for use when the modal is already open.
    setFenixModalContent(true, modalBody, true, true, true, true);
}

export async function showOriginSelectModal(stageId=null, routeId=null, direction=null, selectedOriginId=null) {
    log('showOriginSelectModal', stageId, routeId, direction, selectedOriginId);

    if(!routeId) {
        log('showOriginSelectModal: Route ID required');
        return false;
    }

    // Make sure the Fenix modal has a backdrop.
    await setFenixBackdrop(true);

    // Update the Fenix modal with the Origin Select modal content.
    
    const modalTitle = t('selectOrigin');
    const modalFooter = getTemplate('fenixDefaultFooterTemplate'); // Use the default footer template and customize it.
    const modalDesktopSize = 'lg';
    const modalMobileHeight = null; //Use default height.
    const modalDataPurpose = 'originSelect'; // To ID this modal content for JS/CSS selectors. This is used to identify the modal content for JS/CSS selectors.

    // Customize the default modal footer...
    modalFooter.find('[data-label="fenixDefaultFooterSaveButton"]').text(t('selectOrigin')).attr('data-action', 'originSelectModal_submit');

    // Load animated bootstrap placeholder for loading.
    setFenixModalContent(modalTitle, getTemplate('loadingPlaceholder'), modalFooter, modalDesktopSize, modalMobileHeight, modalDataPurpose);
    console.time('openFenixModal');
    await openFenixModal();
    console.timeEnd('openFenixModal');
    await nextFrame(); //wait for the next frame to ensure the modal is open before continuing.
    await delay(100); //wait N milliseconds for testing.

    // Get Body Template
    let modalBody = getTemplate('placeSelectModalTemplate');

    // Start customizing the modal body...
    const listType = 'origin';

    const $placeList = modalBody.find('ul[data-label="place-list-container"]');

    // Add stage-id and purpose data atttribute
    $placeList.attr('data-stage-id', stageId);
    $placeList.attr('data-purpose', 'originSelect');
    $placeList.attr('data-list-type', listType);
    $placeList.attr('data-cache-key', 'originSelect');

    // Build the route places list every time the modal is opened.
    const allRoutePlaces = getRoutePlaces(routeId, direction, null);

    // Add child route places to the route places.
    let filteredRoutePlaces = addChildRoutePlaces(allRoutePlaces, direction);

    // Save to global var (for future reference, e.g. search filtering)
    setGlobalPathStops('originSelect', filteredRoutePlaces);

    log('filteredRoutePlaces', filteredRoutePlaces, listType);
    const listFragment = renderFilteredPlaceList(filteredRoutePlaces, listType, selectedOriginId, null, null, null, routeId, false, true, true, false, true);
    
    $placeList.empty().append(listFragment);

    // Hook up search filtering
    const $search = modalBody.find('input[data-label="placeListSearch"]');
    $search.val(''); //Clear search input
    $search.off('input').on('input', function () {
        log('placeSearchLabel input', $(this).val());
        const cachedRoutePlaces = getGlobalPathStops('originSelect');
        if(cachedRoutePlaces) {
            if($(this).val().length > 0) {
                // Make mutable copy of cachedRoutePlaces
                const mutableCachedRoutePlaces = JSON.parse(JSON.stringify(cachedRoutePlaces));
                const filteredRoutePlaces = hideRoutePlacesBySearchQuery(mutableCachedRoutePlaces, $(this).val());
                const listFragment = renderFilteredPlaceList(filteredRoutePlaces, listType, selectedOriginId, null, null, null, routeId, true, true, true, false, true);
                $placeList.empty().append(listFragment);
            } else {
                // Revert to original list
                const listFragment = renderFilteredPlaceList(filteredRoutePlaces, listType, selectedOriginId, null, null, null, routeId, false, true, true, false, true);
                $placeList.empty().append(listFragment);
            }
        }
    });
    
    // End customizing the modal body...


    // Set the modal content
    console.time('setFenixModalContent');
    setFenixModalContent(true, modalBody, true, true, true, true);
    console.timeEnd('setFenixModalContent');

    //Scroll to selected place if set
    if(selectedOriginId) {
        const selectedPlaces = document.getElementsByClassName('scroll-to-preselected-place');
        if(selectedPlaces.length > 0) {
            selectedPlaces[0].scrollIntoView({ behavior: 'smooth' });
        }
    }
}
// window.showOriginSelectModal = showOriginSelectModal;

export async function showDestinationSelectModal(stageId=null, routeId=null, direction=null, originId=null, selectedDestinationId=null, selectedViaIds=null) {
    log('showDestinationSelectModal', stageId, routeId, direction, originId, selectedDestinationId, selectedViaIds);

    if(!routeId || !originId) {
        log('showDestinationSelectModal: Route ID, and Origin ID required');
        return false;
    }

    // Make sure the Fenix modal has a backdrop.
    await setFenixBackdrop(true);

    // Update the Fenix modal with the Origin Select modal content.
    
    const modalTitle = t('selectDestination');
    const modalFooter = getTemplate('fenixDefaultFooterTemplate'); // Use the default footer template and customize it.
    const modalDesktopSize = 'lg';
    const modalMobileHeight = null; //Use default height.
    const modalDataPurpose = 'destinationSelect'; // To ID this modal content for JS/CSS selectors. This is used to identify the modal content for JS/CSS selectors.

    // Customize the default modal footer...
    modalFooter.find('[data-label="fenixDefaultFooterSaveButton"]').text(t('selectDestination')).attr('data-action', 'destinationSelectModal_submit');

    // Load animated bootstrap placeholder for loading.
    setFenixModalContent(modalTitle, getTemplate('loadingPlaceholder'), modalFooter, modalDesktopSize, modalMobileHeight, modalDataPurpose);
    console.time('openFenixModal');
    await openFenixModal();
    console.timeEnd('openFenixModal');
    await nextFrame(); //wait for the next frame to ensure the modal is open before continuing.
    await delay(100); //wait N milliseconds for testing.

    // Get Body Template
    let modalBody = getTemplate('placeSelectModalTemplate');

    // Start customizing the modal body...

    const $placeList = modalBody.find('ul[data-label="place-list-container"]');

    // Build the route places list every time the modal is opened.
    const allRoutePlaces = getRoutePlaces(routeId, direction, originId);
    log('allRoutePlaces', allRoutePlaces);

    let listType = 'destination';

    // Add stage-id and purpose data atttribute
    $placeList.attr('data-stage-id', stageId);
    $placeList.attr('data-purpose', 'destinationSelect');
    $placeList.attr('data-list-type', listType);
    $placeList.attr('data-cache-key', 'destinationSelect');

    // Add child route places to the route places.
    let filteredRoutePlaces = addChildRoutePlaces(allRoutePlaces, direction);

    // Slice the route places from the origin.
    if(originId) {
        filteredRoutePlaces = sliceRoutePlacesFromOrigin(filteredRoutePlaces, originId);

        // Add calculated values from the origin (Distance, Ascending, Descending)
        filteredRoutePlaces = addCalculatedValuesFromOrigin(filteredRoutePlaces, routeId, originId);
    }

    // Save to global var (for future reference, e.g. search filtering)
    setGlobalPathStops('destinationSelect', filteredRoutePlaces);

    // log('filteredRoutePlaces', filteredRoutePlaces, listType);
    const listFragment = renderFilteredPlaceList(filteredRoutePlaces, listType, selectedDestinationId, originId, null, selectedViaIds, routeId, false, true, true, true, false);
    
    // Write list to the ul container.
    $placeList.empty().append(listFragment);

    // Hook up search filtering
    const $search = modalBody.find('input[data-label="placeListSearch"]');
    $search.val(''); //Clear search input
    $search.off('input').on('input', function () {
        log('placeSearchLabel input', $(this).val());
        const cachedRoutePlaces = getGlobalPathStops('destinationSelect');
        if(cachedRoutePlaces) {
            if($(this).val().length > 0) {
                // Make mutable copy of cachedRoutePlaces
                const mutableCachedRoutePlaces = JSON.parse(JSON.stringify(cachedRoutePlaces));
                log('mutableCachedRoutePlaces: ', mutableCachedRoutePlaces);
                const accessiblePlaces = filterInaccessiblePlaces(mutableCachedRoutePlaces, routeId, originId); // If origin is on a child route, filter out the places that are in the shadow of the origin child route. (Vice versa not required because the SPLIT which contains child places comes before the origin place).
                log('accessiblePlaces: ', accessiblePlaces);
                const filteredRoutePlaces = hideRoutePlacesBySearchQuery(accessiblePlaces, $(this).val());
                log('filteredBySearchQueryRoutePlaces: ', filteredRoutePlaces);
                const listFragment = renderFilteredPlaceList(filteredRoutePlaces, listType, selectedDestinationId, originId, null, selectedViaIds, routeId, true, true, true, true, false);
                $placeList.empty().append(listFragment);
            } else {
                // Revert to original list
                const listFragment = renderFilteredPlaceList(filteredRoutePlaces, listType, selectedDestinationId, originId, null, selectedViaIds, routeId, false, true, true, true, false);
                $placeList.empty().append(listFragment);
            }
        }
    });
    
    // End customizing the modal body...


    // Set the modal content
    console.time('setFenixModalContent');
    setFenixModalContent(true, modalBody, true, true, true, true);
    console.timeEnd('setFenixModalContent');

    //Scroll to selected place if set
    if(selectedDestinationId) {
        const selectedPlaces = document.getElementsByClassName('scroll-to-preselected-place');
        if(selectedPlaces.length > 0) {
            selectedPlaces[0].scrollIntoView({ behavior: 'smooth' });
        }
    }
}

export async function showCreateNewItineraryPopup() {
    log('showCreateNewItineraryPopup');
    
    // Make sure the Fenix modal has a backdrop.
    await setFenixBackdrop(true);

    // Update the Fenix modal with the Route Select modal content.
    const modalTitle = t('createNewCamino');
    const modalFooter = getTemplate('fenixDefaultFooterTemplate'); // Use the default footer template and customize it.
    const modalDesktopSize = 'sm';
    const modalMobileHeight = 'short';
    const modalDataPurpose = 'createNewItinerary'; // To ID this modal content for JS/CSS selectors. This is used to identify the modal content for JS/CSS selectors.
    
    // Customize the default modal footer...
    modalFooter.find('[data-label="fenixDefaultFooterSaveButton"]').text(t('create')).attr('data-action', 'createNewItineraryModal_submit');

    // Load animated bootstrap placeholder for loading.
    setFenixModalContent(modalTitle, getTemplate('loadingPlaceholder'), modalFooter, modalDesktopSize, modalMobileHeight, modalDataPurpose);
    await openFenixModal();
    await nextFrame(); //wait for the next frame to ensure the modal is open before continuing.
    
    let modalBody = getTemplate('createNewItineraryModalTemplate');
    
    // Start customizing the modal body...
    
    // Set the modal content. True means don't change it - for use when the modal is already open.
    setFenixModalContent(true, modalBody, true, true, true, true);
}

export async function createNewItinerary(title) {
    log('createNewItinerary', title);
    
    const payload = {
        title: title,
        _ajax_nonce: window.ironman.gnonce
    };
    
    const response = await callAdminAjax('createItinerary', payload, 'createItinerary', null, 'page-blocking', t('creatingNewCamino'));

    if(response.success && response.data.new_post_id) {
        // Redirect to the new itinerary page.
        log('Redirecting to new itinerary page', response.data.new_itinerary_url);
        window.location.href = response.data.new_itinerary_url;
        return true;
    }

    log('Error creating itinerary: ', response);
    return false;
}

export async function openPaceSliderPopup() {
    log('openPaceSliderPopup');
    
    // Make sure the Fenix modal has a backdrop.
    await setFenixBackdrop(true);

    const modalTitle = t('setEstimatedTravelSpeed');
    const modalFooter = getTemplate('fenixDefaultFooterTemplate');
    const modalDesktopSize = 'xs';
    const modalMobileHeight = 'short';
    const modalDataPurpose = 'paceSlider'; // To ID this modal content for JS/CSS selectors. This is used to identify the modal content for JS/CSS selectors.
    
    // Customize the default modal footer...
    modalFooter.find('[data-label="fenixDefaultFooterSaveButton"]').text(t('save')).attr('data-action', 'paceSliderModal_submit');

    // Load animated bootstrap placeholder for loading.
    setFenixModalContent(modalTitle, getTemplate('loadingPlaceholder'), modalFooter, modalDesktopSize, modalMobileHeight, modalDataPurpose);
    await openFenixModal();
    await nextFrame(); //wait for the next frame to ensure the modal is open before continuing.
    
    let modalBody = getTemplate('paceSliderModalTemplate');
    
    // Start customizing the modal body...

    // Set range slider values
    const $rangeSlider = modalBody.find('[data-label="range-slider"]');
    $rangeSlider.attr('data-range-slider-min', 3);
    $rangeSlider.attr('data-range-slider-max', 8);
    $rangeSlider.attr('data-range-slider-step', '.1');
    $rangeSlider.attr('data-range-slider-units', 'km');
    $rangeSlider.attr('data-range-slider-start', window.ironman.paceKmh);
    // End customizing the modal body...

    // Set the modal content. True means don't change it - for use when the modal is already open.
    setFenixModalContent(true, modalBody, true, true, true, true);

    // Initialize the range sliders in the modal element.
    const modalElement = getFenixModalElement();
    initRangeSliders(modalElement);
}